import React, { useState } from 'react'
import { formatCurrencyValue } from '@vend/utilities'
import {
  Button,
  Icon,
  Popover,
  Text,
  WithPopover,
} from '@lightspeed/design-system-react'
import { T } from '@transifex/react'
import { useAppSelector } from '../../../state/hooks'
import {
  selectSalePayments,
  selectSaleTotalsServiceFee,
} from '../../../state/slices/currentSale/currentSaleSlice'
import { getPaymentsTotal } from '../../../functions/sale'
import {
  useRegisterManager,
  useRetailerSettingsManager,
  useSaleManager,
} from '../../../ng-compat/hooks'

interface ServiceFeePopoverProps {
  onClose: () => void
}

const ServiceFeePopover: React.FC<ServiceFeePopoverProps> = ({ onClose }) => {
  const registerManager = useRegisterManager()
  const serviceFee = useAppSelector(selectSaleTotalsServiceFee)
  const retailer = useRetailerSettingsManager().get()
  const currentOutlet = registerManager.getActiveOutlet()
  const saleManager = useSaleManager()

  const handleDeleteServiceFee = () => {
    saleManager.removeServiceFeeFromSale()
    onClose()
  }

  return (
    <Popover className="sale-popover" onClose={onClose}>
      <h1 className="vd-text-label">
        <T _str="Remove service fee" _context="Sale footer service fee" />
      </h1>
      <hr className="vd-hr vd-mb0" />
      <div className="wr-sale-popover-item vd-flex--align-center">
        <div>
          <T _str="Service fee" />{' '}
          {`(${registerManager.getDisplayCashDiscountingRate()}%)`}
        </div>
        <div>
          <span className="vd-mr1">
            {formatCurrencyValue(
              serviceFee,
              retailer.culture,
              currentOutlet.currency,
              currentOutlet.currency_symbol
            )}
          </span>
          <Button
            type="button"
            variant="supplementary"
            modifier="icon"
            onClick={handleDeleteServiceFee}
            aria-label="Delete service fee"
            data-track="delete-service-fee"
          >
            <Icon icon="fa-trash" />
          </Button>
        </div>
      </div>
    </Popover>
  )
}

export const ServiceFee: React.FC = () => {
  const [displayServiceFeePopover, setDisplayServiceFeePopover] = useState(
    false
  )
  const payments = useAppSelector(selectSalePayments)
  const serviceFee = useAppSelector(selectSaleTotalsServiceFee)
  const retailer = useRetailerSettingsManager().get()
  const registerManager = useRegisterManager()
  const currentOutlet = registerManager.getActiveOutlet()
  const hasNonZeroSumPayments = getPaymentsTotal(payments) !== 0

  return (
    <>
      <div>
        {!hasNonZeroSumPayments ? (
          <WithPopover direction="left">
            <div>
              <Text
                className="vd-link"
                type="label"
                onClick={() => setDisplayServiceFeePopover(true)}
              >
                <T _str="Service fee" _context="Sale footer service fee" />
              </Text>
              <span className="sale-total-line-item--sub">
                {registerManager.getDisplayCashDiscountingRate()}%
              </span>
            </div>

            {displayServiceFeePopover ? (
              <ServiceFeePopover
                onClose={() => setDisplayServiceFeePopover(false)}
              />
            ) : null}
          </WithPopover>
        ) : null}

        {hasNonZeroSumPayments ? (
          <Text type="label">
            <T _str="Service fee" _context="Sale footer service fee" />
          </Text>
        ) : null}
      </div>

      <Text type="label">
        {formatCurrencyValue(
          serviceFee,
          retailer.culture,
          currentOutlet.currency,
          currentOutlet.currency_symbol
        )}
      </Text>
    </>
  )
}
